import type { AdminGame, MediaRef } from '@mcgames/contracts';
import { Button } from '@mcgames/ui';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorText } from './ErrorText.tsx';
import { FileButton } from './FileButton.tsx';
import { useGameMutation, useMetadataStatus } from './game-hooks.ts';
import styles from './Games.module.css';
import { IMAGE_ACCEPT, IMAGE_KINDS, imageForKind, type ImageKind } from './media-kinds.ts';
import { SgdbIdentify } from './SgdbIdentify.tsx';
import { SgdbPicker } from './SgdbPicker.tsx';

function mediaForm(kind: string, file: File): FormData {
  const form = new FormData();
  form.append('kind', kind);
  form.append('file', file);
  return form;
}

export function ArtworkTab({ game }: { game: AdminGame }) {
  const metadata = useMetadataStatus();
  const [sgdbKind, setSgdbKind] = useState<ImageKind | null>(null);
  const sgdbEnabled = metadata.data?.steamgriddb === true;

  return (
    <>
      {metadata.data !== undefined && <SgdbIdentify game={game} sgdbEnabled={sgdbEnabled} />}
      <div className={styles['slots']}>
        {IMAGE_KINDS.map((kind) => (
          <ImageSlot
            key={kind}
            game={game}
            kind={kind}
            sgdbEnabled={sgdbEnabled}
            onSearchSgdb={() => setSgdbKind(kind)}
          />
        ))}
      </div>
      <Screenshots game={game} />
      {sgdbKind !== null && (
        <SgdbPicker game={game} kind={sgdbKind} onClose={() => setSgdbKind(null)} />
      )}
    </>
  );
}

type ImageSlotProps = {
  game: AdminGame;
  kind: ImageKind;
  sgdbEnabled: boolean;
  onSearchSgdb: () => void;
};

function ImageSlot({ game, kind, sgdbEnabled, onSearchSgdb }: ImageSlotProps) {
  const { t } = useTranslation('admin');
  const headingId = useId();
  const upload = useGameMutation<FormData>(game.id, 'POST', '/media');
  const url = imageForKind(game, kind);

  return (
    <section className={styles['slot']} aria-labelledby={headingId}>
      <h3 id={headingId} className={styles['subheading']}>
        {t(`games.kinds.${kind}`)}
      </h3>
      <div className={styles['preview']}>
        {url !== null ? (
          <img
            className={styles['previewImage']}
            src={url}
            alt={t(`games.artwork.current.${kind}`)}
          />
        ) : (
          <p className={styles['muted']}>{t('games.artwork.none')}</p>
        )}
      </div>
      <div className={styles['row']}>
        {sgdbEnabled && (
          <Button variant="secondary" size="sm" onClick={onSearchSgdb}>
            {t('games.artwork.searchSgdb')}
          </Button>
        )}
        <FileButton
          label={t('games.artwork.upload')}
          accept={IMAGE_ACCEPT}
          disabled={upload.isPending}
          onFiles={([file]) => {
            if (file !== undefined) upload.mutate(mediaForm(kind, file));
          }}
        />
      </div>
      {upload.isPending && <p className={styles['hint']}>{t('games.artwork.uploading')}</p>}
      <ErrorText error={upload.error} />
    </section>
  );
}

function Screenshots({ game }: { game: AdminGame }) {
  const { t } = useTranslation('admin');
  const headingId = useId();
  const upload = useGameMutation<FormData>(game.id, 'POST', '/media');
  const reorder = useGameMutation<{ ids: string[] }>(game.id, 'PATCH', '/media/order');
  const [uploadError, setUploadError] = useState<unknown>(null);
  const shots = game.screenshots;

  async function uploadAll(files: File[]) {
    setUploadError(null);
    try {
      // One request per file: the endpoint takes a single image.
      for (const file of files) await upload.mutateAsync(mediaForm('screenshot', file));
    } catch (error) {
      setUploadError(error);
    }
  }

  function move(index: number, offset: -1 | 1) {
    const ids = shots.map((shot) => shot.id);
    const other = ids[index + offset];
    const current = ids[index];
    if (other === undefined || current === undefined) return;
    ids[index + offset] = current;
    ids[index] = other;
    reorder.mutate({ ids });
  }

  return (
    <section className={styles['page']} aria-labelledby={headingId}>
      <h3 id={headingId} className={styles['subheading']}>
        {t('games.artwork.screenshots')}
      </h3>
      <div className={styles['row']}>
        <FileButton
          label={t('games.artwork.uploadScreenshots')}
          accept={IMAGE_ACCEPT}
          multiple
          disabled={upload.isPending}
          onFiles={(files) => void uploadAll(files)}
        />
        {upload.isPending && <span className={styles['hint']}>{t('games.artwork.uploading')}</span>}
      </div>
      <ErrorText error={uploadError ?? reorder.error} />
      {shots.length === 0 ? (
        <p className={styles['muted']}>{t('games.artwork.noScreenshots')}</p>
      ) : (
        <ul className={styles['shots']}>
          {shots.map((shot, index) => (
            <ScreenshotItem
              key={shot.id}
              gameId={game.id}
              shot={shot}
              position={index + 1}
              isLast={index === shots.length - 1}
              busy={reorder.isPending}
              onMove={(offset) => move(index, offset)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

type ScreenshotItemProps = {
  gameId: string;
  shot: MediaRef;
  position: number;
  isLast: boolean;
  busy: boolean;
  onMove: (offset: -1 | 1) => void;
};

function ScreenshotItem({ gameId, shot, position, isLast, busy, onMove }: ScreenshotItemProps) {
  const { t } = useTranslation('admin');
  const remove = useGameMutation<void>(gameId, 'DELETE', `/media/${encodeURIComponent(shot.id)}`);

  return (
    <li className={styles['shot']}>
      <img
        className={styles['shotImage']}
        src={shot.url}
        alt={t('games.artwork.screenshotAlt', { position })}
        loading="lazy"
      />
      <div className={styles['row']}>
        <Button
          variant="secondary"
          size="sm"
          disabled={busy || position === 1}
          aria-label={t('games.artwork.moveUp', { position })}
          onClick={() => onMove(-1)}
        >
          <span aria-hidden="true">↑</span>
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={busy || isLast}
          aria-label={t('games.artwork.moveDown', { position })}
          onClick={() => onMove(1)}
        >
          <span aria-hidden="true">↓</span>
        </Button>
        <Button
          variant="danger"
          size="sm"
          disabled={remove.isPending}
          aria-label={t('games.artwork.delete', { position })}
          onClick={() => remove.mutate()}
        >
          <span aria-hidden="true">×</span>
        </Button>
      </div>
      <ErrorText error={remove.error} />
    </li>
  );
}
